import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { useMe } from '../hooks/useMe'
import { useEventFinancials, useEventIncome } from '../hooks/useFinancials'
import { ApiClient } from '../api'
import { formatGBP } from '../lib/money'
import { RevenueBar } from './charts/RevenueBar'
import { SimpleLine } from './charts/SimpleLine'
import type { EventIncomePayload } from '../types'

// FinancialsTab — Sales seq Part 2. Read-only analytics surface served by
// GET /api/events/{id}/financials. Mirrors the v5 prototype's FinancialsTab
// (HTML lines 1724-1762):
//   - 4 KPI tiles (Revenue, Total Costs, Net Margin, Avg Spend) with a
//     coloured left accent stripe per the prototype CSS lines 96-105.
//   - 2-column grid of breakdown cards (Revenue by ticket class, Costs by
//     category) using the RevenueBar bar component.
//   - Full-width Live Sales Feed card with a SimpleLine SVG sparkline of
//     tickets sold per operating day (deviated from the prototype's "last
//     24h" hourly framing — the underlying data model doesn't support
//     hour-level granularity post-Part-1, see handler header for why).
//
// Gating: hard-stop for non-financial users (mirrors BudgetTab.tsx lines
// 45-61) so a direct URL hit still renders a friendly locked card rather
// than blowing through to a 403 page or blank tab. The server also
// returns 403 — defence in depth.

interface Props {
  eventId: number
}

const ACCENTS = {
  green: '#0a4a3a',
  brass: '#9b7f2a',
  oxblood: '#7a2329',
  slate: '#3d5a6c',
} as const

type Accent = keyof typeof ACCENTS

export function FinancialsTab({ eventId }: Props) {
  const { data: me } = useMe()
  const { data, isLoading, error, refetch, isRefetching } = useEventFinancials(eventId)

  // Hard-stop for non-financial users — same locked-card pattern as BudgetTab.
  // visibleTabs in EventDetail.tsx already hides the tab strip entry, this
  // is belt + braces for direct URL hits.
  if (me && !me.hasFinancial) return <LockedCard />

  if (isLoading) return <FinancialsSkeleton />

  if (error) {
    if (error instanceof Error && error.message.startsWith('403')) return <LockedCard />
    return (
      <div className="bg-oxblood-soft border border-oxblood text-oxblood rounded p-4 text-sm">
        <div className="font-semibold mb-1">Could not load financials</div>
        <pre className="text-xs whitespace-pre-wrap mb-3">{(error as Error).message}</pre>
        <button
          type="button"
          onClick={() => refetch()}
          disabled={isRefetching}
          className="px-3 py-1 text-xs font-semibold rounded border border-oxblood text-oxblood hover:bg-oxblood/10 disabled:opacity-50"
        >
          {isRefetching ? 'Retrying…' : 'Retry'}
        </button>
      </div>
    )
  }

  if (!data) return null

  const { kpis, revenueByClass, costsByCategory, salesByDay, netPending, asOf } = data
  const noRevenue = revenueByClass.length === 0
  const noCosts = costsByCategory.length === 0

  if (kpis.ticketsSold === 0 && noCosts) {
    return <EmptyAll asOf={asOf} />
  }

  return (
    <div className="space-y-4">
      {/* Forecast (projected) — read-only mirror of the Forecast Studio idea. */}
      <ForecastProjectionPanel eventId={eventId} />

      {/* Event Income — online + in-house tickets + on-the-day takings */}
      <IncomePanel eventId={eventId} />

      {/* KPI grid */}
      <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
        <KpiCard
          accent="green"
          label="Revenue (Tickets)"
          value={formatGBP(kpis.revenueGrossPence)}
          trend={`${kpis.ticketsSold.toLocaleString('en-GB')} sold`}
          trendUp
        />
        <KpiCard
          accent="brass"
          label="Total Costs"
          value={formatGBP(kpis.totalCostsPence)}
          trend={costsByCategory.length === 0 ? 'No costs recorded' : `${costsByCategory.length} categor${costsByCategory.length === 1 ? 'y' : 'ies'}`}
        />
        <KpiCard
          accent="slate"
          label="Net Margin"
          value={formatGBP(kpis.netMarginPence)}
          trend={`${kpis.netMarginPct >= 0 ? '+' : ''}${kpis.netMarginPct.toFixed(1)}%`}
          trendUp={kpis.netMarginPct >= 0}
          subtext={netPending ? 'Gross — net pending refunds' : undefined}
        />
        <KpiCard
          accent="oxblood"
          label="Avg. Spend / Visitor"
          value={formatGBP(kpis.avgSpendPerVisitorPence)}
          trend="Inc. tickets only"
        />
      </div>

      {/* Two-column breakdown */}
      <div className="grid gap-4 grid-cols-1 lg:grid-cols-2">
        <BreakdownCard title="Revenue Breakdown">
          {noRevenue ? (
            <PlaceholderRow>No ticket sales recorded for this event yet.</PlaceholderRow>
          ) : (
            revenueByClass.map((r, i) => (
              <RevenueBar
                key={r.ticketClassMeracId}
                label={r.ticketClassName}
                valuePence={r.grossPence}
                pct={r.pctOfRevenue}
                colour="green"
                last={i === revenueByClass.length - 1}
              />
            ))
          )}
        </BreakdownCard>

        <BreakdownCard title="Cost Breakdown">
          {noCosts ? (
            <PlaceholderRow>No budget lines recorded for this event yet.</PlaceholderRow>
          ) : (
            costsByCategory.map((c, i) => (
              <RevenueBar
                key={c.category}
                label={c.category}
                valuePence={c.totalPence}
                pct={c.pctOfCosts}
                colour="oxblood"
                last={i === costsByCategory.length - 1}
              />
            ))
          )}
        </BreakdownCard>
      </div>

      {/* Live sales feed */}
      <div className="bg-white border border-line rounded">
        <div className="px-5 py-4 border-b border-line flex items-center justify-between gap-3">
          <div>
            <h3 className="font-display text-lg">Live Sales Feed</h3>
            <p className="text-xs text-ink-soft mt-0.5">Tickets sold by operating day</p>
          </div>
          <LiveBadge />
        </div>
        <div className="px-5 py-5">
          <SimpleLine
            data={salesByDay.map((d) => ({ value: d.ticketsSold }))}
            height={200}
            emptyLabel={kpis.ticketsSold === 0 ? 'No sales data yet' : 'No per-day breakdown available'}
          />
        </div>
      </div>

      {asOf && (
        <p className="text-[11px] text-ink-soft font-mono text-right pr-1">
          Synced {formatAsOf(asOf)}
        </p>
      )}
    </div>
  )
}

// ---- KPI card -------------------------------------------------------------

function KpiCard({
  accent, label, value, trend, trendUp, subtext,
}: {
  accent: Accent
  label: string
  value: string
  trend: string
  trendUp?: boolean
  subtext?: string
}) {
  return (
    <div className="relative bg-white border border-line rounded-lg overflow-hidden" style={{ padding: '18px 20px' }}>
      <span
        aria-hidden
        className="absolute left-0 top-0 bottom-0"
        style={{ width: 3, background: ACCENTS[accent] }}
      />
      <div className="text-[11.5px] uppercase tracking-wider font-semibold text-ink-soft mb-1.5">
        {label}
      </div>
      <div className="font-display text-2xl font-bold leading-none mb-1.5">{value}</div>
      <div
        className="text-xs"
        style={{ color: trendUp ? '#0a4a3a' : 'var(--tw-text-ink-soft, #5a5a5a)' }}
      >
        {trend}
      </div>
      {subtext && <div className="text-[11px] text-ink-soft mt-1 italic">{subtext}</div>}
    </div>
  )
}

// ---- Breakdown card -------------------------------------------------------

function BreakdownCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white border border-line rounded">
      <div className="px-5 py-4 border-b border-line">
        <h3 className="font-display text-lg">{title}</h3>
      </div>
      <div className="px-5 py-5">{children}</div>
    </div>
  )
}

function PlaceholderRow({ children }: { children: React.ReactNode }) {
  return <div className="text-xs italic text-ink-soft py-2">{children}</div>
}

// ---- Live badge -----------------------------------------------------------
// Matches v5 prototype's `badge status-live` (HTML lines 112 / 1757).

function LiveBadge() {
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11.5px] font-semibold"
      style={{ background: '#f8dbdd', color: '#7a2329' }}
    >
      <span className="nymr-live-pulse-dot" style={{ background: '#7a2329' }} />
      Live
    </span>
  )
}

// ---- Locked card / skeleton / empty states --------------------------------

function LockedCard() {
  return (
    <div className="bg-white border border-line rounded p-10 text-center">
      <div
        className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center"
        style={{ background: '#ece6db', color: '#5a5a5a' }}
      >
        🔒
      </div>
      <h3 className="font-display text-lg mb-1">Financial access required</h3>
      <p className="text-sm text-ink-soft max-w-md mx-auto">
        You don't have financial access to this event. Speak to your event
        manager or admin to be added to the Finance role.
      </p>
    </div>
  )
}

function EmptyAll({ asOf }: { asOf: string | null }) {
  return (
    <div className="bg-white border border-line rounded p-10 text-center">
      <h3 className="font-display text-lg mb-2">No financial data yet</h3>
      <p className="text-sm text-ink-soft max-w-md mx-auto">
        Once tickets start selling or budget lines are added, KPIs and
        breakdowns will populate here.
      </p>
      {asOf && (
        <p className="text-[11px] text-ink-soft font-mono mt-4">
          Last MERAC sync: {formatAsOf(asOf)}
        </p>
      )}
    </div>
  )
}

function FinancialsSkeleton() {
  return (
    <div className="space-y-4">
      <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="bg-white border border-line rounded-lg" style={{ height: 96 }} />
        ))}
      </div>
      <div className="grid gap-4 grid-cols-1 lg:grid-cols-2">
        <div className="bg-white border border-line rounded" style={{ height: 240 }} />
        <div className="bg-white border border-line rounded" style={{ height: 240 }} />
      </div>
      <div className="bg-white border border-line rounded" style={{ height: 240 }} />
    </div>
  )
}

// ---- Forecast (projected) panel -------------------------------------------
// Read-only reference row carrying the figures generated in Forecast Studio for
// this event's idea. Not modifiable here — the Forecast tab / Studio own it.
function ForecastProjectionPanel({ eventId }: { eventId: number }) {
  const q = useQuery({
    queryKey: ['forecast-finance', eventId],
    queryFn: () => ApiClient.forecastFinance(eventId),
    retry: false,
    enabled: Number.isFinite(eventId) && eventId > 0
  })
  const fin = q.data
  if (!fin || !fin.hasForecast) return null
  const net = fin.netPence
  return (
    <div className="bg-white border border-line rounded-lg overflow-hidden">
      <div className="px-5 py-3 border-b border-line flex items-center gap-2">
        <h3 className="font-display text-lg">Forecast (projected)</h3>
        <span className="text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded" style={{ background: '#eef5f1', color: '#0a4a3a' }}>Forecast Studio · read-only</span>
        <Link to={`/events/${eventId}?tab=forecast`} className="ml-auto text-xs font-semibold" style={{ color: '#7a540b' }}>Open Forecast tab →</Link>
      </div>
      <div className="grid gap-px" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', background: '#e7e1d5' }}>
        <ProjFig label="Projected revenue" value={formatGBP(fin.grossRevenuePence)} />
        <ProjFig label="Projected costs" value={formatGBP(fin.totalCostsPence)} />
        <ProjFig label="Projected net" value={formatGBP(net)} accent={net >= 0 ? '#0a4a3a' : '#7a2329'} />
        <ProjFig label="Net incremental" value={formatGBP(fin.netIncrementalPence)} />
      </div>
    </div>
  )
}

function ProjFig({ label, value, accent }: { label: string; value: string; accent?: string }) {
  return (
    <div className="bg-white px-5 py-3">
      <div className="text-[10px] uppercase tracking-wider font-semibold text-ink-soft mb-1">{label}</div>
      <div className="font-display text-xl" style={{ color: accent ?? '#1a1a1a' }}>{value}</div>
    </div>
  )
}

// ---- Event Income panel ---------------------------------------------------
// Ticket income (lens B) = Online (advance web bookings) + In-house (counter /
// walk-up), split from ONE population by MERAC channel so the two never
// overlap (2026-08-10 double-count fix). A = the wider on-the-day till takings
// by category. Fed by the same financial-gated endpoint family as this tab.

function IncomePanel({ eventId }: { eventId: number }) {
  const { data, isLoading, error } = useEventIncome(eventId)
  if (isLoading) return <div className="bg-white border border-line rounded-lg" style={{ height: 150 }} />
  if (error || !data) return null
  return <IncomePanelBody data={data} />
}

function IncomePanelBody({ data }: { data: EventIncomePayload }) {
  const { online, inhouse, refunds, ticketTotalPence, ticketTotalQty, onTheDay, asOf, hasData, source } = data
  const total = onTheDay.totalPence
  const hasRefunds = (refunds?.pence ?? 0) > 0

  return (
    <div className="bg-white border border-line rounded-lg overflow-hidden">
      <div className="px-5 py-3.5 border-b border-line flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <h3 className="font-display text-lg">Event Income</h3>
          <span
            className="text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded"
            style={{ background: '#ece6db', color: '#5a5a5a' }}
          >
            MERAC
          </span>
        </div>
        {asOf && <span className="text-[11px] text-ink-soft font-mono">Synced {formatAsOf(asOf)}</span>}
      </div>

      <div className="px-5 py-5 space-y-5">
        {/* Lens B — ticket income (with the C split) */}
        <div>
          <div className="text-[11.5px] uppercase tracking-wider font-semibold text-ink-soft mb-2">
            Ticket income
          </div>
          <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
            <IncomeTile label="Online" sub={`${online.qty.toLocaleString('en-GB')} advance`} value={formatGBP(online.pence)} />
            <IncomeTile
              label="In-house"
              sub={`${inhouse.qty.toLocaleString('en-GB')} counter / walk-up`}
              value={formatGBP(inhouse.pence)}
            />
            {hasRefunds && (
              <IncomeTile label="Refunds" sub="netted into total" value={`−${formatGBP(refunds.pence)}`} />
            )}
            <IncomeTile label={hasRefunds ? 'Net tickets' : 'Total tickets'} sub={`${ticketTotalQty.toLocaleString('en-GB')} tickets`} value={formatGBP(ticketTotalPence)} emphasis />
          </div>
          <p className="text-[11px] text-ink-soft mt-2">
            Online = advance web bookings · In-house = counter / walk-up{hasRefunds ? ' · Refunds netted into the total' : ''}. One population split by MERAC channel, so the halves never overlap.
            {source === 'consolidated'
              ? ' Net basis, reconciled to the MERAC session-sales report (via the Sync Hub).'
              : ' (Legacy split — this event isn’t paired in the Sync Hub Ticketing tab yet.)'}
          </p>
        </div>

        {/* Lens A — on-the-day takings by category */}
        {hasData && total > 0 && (
          <div className="border-t border-line pt-4">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[11.5px] uppercase tracking-wider font-semibold text-ink-soft">
                On-the-day takings · all tills
              </div>
              <div className="font-display text-base font-bold">{formatGBP(total)}</div>
            </div>
            <div>
              {onTheDay.byCategory.map((c, i) => (
                <RevenueBar
                  key={c.category}
                  label={c.label}
                  valuePence={c.pence}
                  pct={total > 0 ? (c.pence / total) * 100 : 0}
                  colour="green"
                  last={i === onTheDay.byCategory.length - 1}
                />
              ))}
            </div>
            <p className="text-[11px] text-ink-soft mt-2 italic">
              Gross till takings on event days — includes the event tickets above, plus retail, catering and any booking-payment lines.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

function IncomeTile({ label, sub, value, emphasis }: { label: string; sub: string; value: string; emphasis?: boolean }) {
  return (
    <div
      className="rounded-lg border px-4 py-3"
      style={emphasis ? { borderColor: '#0a4a3a', background: '#f0f5f2' } : { borderColor: '#e6e0d4', background: '#fff' }}
    >
      <div className="text-[11px] uppercase tracking-wide font-semibold text-ink-soft">{label}</div>
      <div
        className={'font-display font-bold leading-none mt-1 ' + (emphasis ? 'text-2xl' : 'text-xl')}
        style={emphasis ? { color: '#0a4a3a' } : undefined}
      >
        {value}
      </div>
      <div className="text-[11px] text-ink-soft mt-1">{sub}</div>
    </div>
  )
}

// ---- Helpers --------------------------------------------------------------

function formatAsOf(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  const time = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
  const date = d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })
  return `${time} on ${date}`
}

